import { useEffect, useState, useRef } from "react";
import { FaCog } from "react-icons/fa";
import Play from "./Components/playWidget";
import Inbox from "./Components/Inbox";
import Drawer from "../../mainComponents/Drawer";
import ShareDrawerContent from "./Components/shareDawer";
import { Link, useNavigate } from "react-router-dom";
import { useLazyGetUserQuery, useLoginMutation, } from "../../redux/auth";
import { useDeviceInfo } from "../../hooks/useDeviceInfo";
import { secureStorageUtils } from "../../utils/secureStorageUtils";
import PlayScreenSkeleton from "./Components/SkelentonLoader";
import { logout } from "../../utils/authUtils";


const PlayScreen = () => {
  const secureStorage = secureStorageUtils;
  const [activeTab, setActiveTab] = useState<"play" | "inbox">("play");
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [profileImage, setProfileImage] = useState<string>("");
  const [shareLink, setShareLink] = useState<string>("");

  const [triggerGetUser] = useLazyGetUserQuery();
  const [login] = useLoginMutation();
  const deviceInfo = useDeviceInfo();
  const navigate = useNavigate();

  const [isUserReady, setIsUserReady] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(
    localStorage.getItem("hasFetchedUser") !== "true"
  );

  // Track when Play is actually shown
  const [isPlayMounted, setIsPlayMounted] = useState(false);

  const initAuth = async () => {
    const hasCachedUser = localStorage.getItem("hasFetchedUser") === "true";
    const cachedUser = secureStorage.getItem("userData");

    if (hasCachedUser && cachedUser) {
      const parsed = JSON.parse(cachedUser);
      setUsername(parsed.username);
      setIsUserReady(true);
      setIsAuthLoading(false);
      return;
    }

    try {
      let userdata: any;
      const token = secureStorage.getItem("access-token");

      if (token) {
        try {
          userdata = await triggerGetUser().unwrap();
        } catch (error) {
          secureStorage.removeItem("access-token");
          await tryAutoLogin();
          userdata = await triggerGetUser().unwrap();
        }
      } else {
        await tryAutoLogin();
        userdata = await triggerGetUser().unwrap();
      }

      setUsername(userdata.data.username);
      secureStorage.setItem("userData", JSON.stringify(userdata.data));
      localStorage.setItem("hasFetchedUser", "true");
      setIsUserReady(true);
    } catch (error: any) {
      console.error("Auth error:", error);
      
      // Check if it's a 401 error
      if (error?.status === 401 || error?.data?.status === 401) {
        console.log("401 Unauthorized detected - logging out user");
        logout(true); // Show toast for manual logout
      } else {
        // For other errors, just clear the current session
        secureStorage.removeItem("access-token");
        localStorage.removeItem("hasFetchedUser");
      }
      
      navigate("/age");
    } finally {
      setIsAuthLoading(false);
    }
  };

  const tryAutoLogin = async () => {
    const loginPayload = {
      userAgent: deviceInfo.userAgent,
      deviceName: deviceInfo.deviceName,
      deviceIP: deviceInfo.deviceIP,
      deviceMacAddress: deviceInfo.deviceMacAddress,
    };

    try {
      const response = await login(loginPayload).unwrap();
      if (response?.data) {
        secureStorage.setItem("access-token", response.data);
      }
    } catch (error: any) {
      console.error("Auto login error:", error);
      
      // If auto login fails with 401, logout and redirect
      if (error?.status === 401 || error?.data?.status === 401) {
        console.log("Auto login failed with 401 - logging out");
        logout(true); // Show toast for auto login failure
        navigate("/age");
      }
    }
  };

  useEffect(() => {
    if (deviceInfo.deviceIP !== "Loading...") {
      initAuth();
    }
  }, [deviceInfo]);

  const handleOpenShareDrawer = () => {
    console.log("handleOpenShareDrawer called, setting drawer to open");
    setIsShareDrawerOpen(true);
  };

  // Tabs drag scroll logic
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!tabsContainerRef.current) return;
    isDraggingRef.current = true;
    startXRef.current = e.pageX - tabsContainerRef.current.offsetLeft;
    scrollLeftRef.current = tabsContainerRef.current.scrollLeft;
    tabsContainerRef.current.style.cursor = "grabbing";
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const x = e.pageX - tabsContainerRef.current!.offsetLeft;
    const walk = (x - startXRef.current) * 1.5;
    tabsContainerRef.current!.scrollLeft = scrollLeftRef.current - walk;
  };

  const endDrag = () => {
    isDraggingRef.current = false;
    if (tabsContainerRef.current) {
      tabsContainerRef.current.style.cursor = "grab";
    }
  };

  useEffect(() => {
    const tabsContainer = tabsContainerRef.current;
    if (tabsContainer) {
      tabsContainer.addEventListener("mousemove", handleMouseMove);
      tabsContainer.addEventListener("mouseup", endDrag);
      tabsContainer.addEventListener("mouseleave", endDrag);
      return () => {
        tabsContainer.removeEventListener("mousemove", handleMouseMove);
        tabsContainer.removeEventListener("mouseup", endDrag);
        tabsContainer.removeEventListener("mouseleave", endDrag);
      };
    }
  }, []);

  return (
    <div className="h-screen bg-white flex flex-col items-center p-2 pt-10 overflow-hidden font-custom">
      {/* Tabs & Header */}
      <div className="w-full max-w-xl relative flex items-center justify-between px-6 mb-6">
        <div
          ref={tabsContainerRef}
          className="flex-1 flex justify-center gap-12 overflow-x-auto no-scrollbar cursor-grab"
          onMouseDown={handleMouseDown}
        >
          <button
            onClick={() => setActiveTab("play")}
            className={`text-lg font-bold whitespace-nowrap ${activeTab === "play" ? "text-black" : "text-gray-300"}`}
          >
            PLAY
          </button>
          <button
            onClick={() => setActiveTab("inbox")}
            className={`text-lg font-bold whitespace-nowrap ${activeTab === "inbox" ? "text-black" : "text-gray-300"}`}
          >
            INBOX
          </button>
        </div>

        {/* Settings */}
        <div className="absolute right-0 flex items-center gap-2 bg-gray-200 px-2 py-1 rounded-full">
          <Link to="/settings">
            <FaCog className="text-gray-500 text-xl" />
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-xl mx-auto">
        {isAuthLoading || !isUserReady ? (
          <PlayScreenSkeleton />
        ) : activeTab === "play" ? (
          <Play
            handleOpenShareDrawer={handleOpenShareDrawer}
            username={username}
            activeCardIndex={activeCardIndex}
            setActiveCardIndex={setActiveCardIndex}
            onMount={() => setIsPlayMounted(true)} // ✅ Add this prop to trigger drawer only when Play is mounted
            onProfileImageChange={setProfileImage}
            onShareLinkChange={setShareLink}
          />
        ) : (
          <Inbox />
        )}
      </div>

      {/* Drawer only after everything is fully ready */}
      {(() => {
        console.log("Drawer render check - isPlayMounted:", isPlayMounted, "isShareDrawerOpen:", isShareDrawerOpen);
        return null;
      })()}
      {isPlayMounted && (
        <Drawer
          isBlur={true}
          isOpen={isShareDrawerOpen}
          position="bottom"
          onClose={() => {
            console.log("Drawer closing");
            setIsShareDrawerOpen(false);
          }}
        >
          <ShareDrawerContent 
            profileImage={profileImage}
            username={username}
            shareLink={shareLink}
          />
        </Drawer>
      )}
    </div>
  );
};

export default PlayScreen;
